import { BufferGeometry, DoubleSide, Float32BufferAttribute, Group, Mesh, MeshBasicMaterial, Points } from "three";
import { Line2 } from "three/addons/lines/Line2.js";
import { fatLine, MEASURE_COLOR, pointsMesh, vec, type SavedMeasurement } from "./MeasureTool";
import type { Viewer } from "./Viewer";

/** Drawing order among this layer's own transparent objects — kept below `MeasureTool`'s own
 *  (10/20/21/30/31), so whichever measurement is actively loaded/edited there stays clearly
 *  on top instead of blending into the rest of the site's saved history. */
const ORDER = { fill: 1, line: 2, points: 3 };
/** Faded relative to the actively-edited shape (which draws at full strength) — a saved
 *  measurement shown "for context" shouldn't compete with the one actually being worked on. */
const FILL_OPACITY = 0.16;
const LINE_OPACITY = 0.55;

/**
 * Draws every measurement saved against the current dataset as a static, read-only shape —
 * the whole point being that a site's Ausmass history is visible at a glance, not only the
 * one measurement picked from the list into `MeasureTool`. Never interactive: dragging,
 * undo, finish and friends only ever apply to the one shape actually loaded there.
 *
 * Points are placed the same way `MeasureTool` places a restored one — flush with the
 * terrain's flat drawn height when terrain is loaded (see `Viewer.terrainDisplayY`), so this
 * layer's shapes sit at the same height as `MeasureTool`'s own and never look like they're
 * floating off the surface everything else is drawn on.
 */
export class SavedMeasurementsLayer {
  private readonly group = new Group();
  private visuals: Array<Points | Line2 | Mesh> = [];

  constructor(private readonly viewer: Viewer) {
    this.group.name = "saved-measurements";
    viewer.world.add(this.group);
  }

  /** Redraws every entry in `list`, skipping `excludeId` — the measurement currently live in
   *  `MeasureTool`, which already draws itself and would otherwise be shown twice (once
   *  editable at full strength, once faded underneath). Safe to call with an empty origin;
   *  a measurement with no scene origin set yet simply can't be placed and is skipped. */
  set(list: SavedMeasurement[], excludeId: string | null): void {
    this.disposeVisuals();
    if (!this.viewer.origin.isSet) return;
    for (const measurement of list) {
      if (measurement.id === excludeId) continue;
      this.buildOne(measurement);
    }
    this.viewer.requestRender();
  }

  dispose(): void {
    this.disposeVisuals();
    this.viewer.world.remove(this.group);
  }

  private buildOne(measurement: SavedMeasurement): void {
    if (measurement.points.length === 0) return;
    const flatY = this.viewer.terrainDisplayY;
    const points = measurement.points.map((p) => {
      const local = this.viewer.origin.toScene(p.east, p.north, p.height);
      return flatY === null ? local : local.setY(flatY);
    });
    const color = MEASURE_COLOR[measurement.mode];

    this.addVisual(pointsMesh(points, color, 6), ORDER.points);

    if (points.length >= 2) {
      const loop = measurement.mode !== "line" ? [...points, points[0]] : points;
      this.addVisual(fatLine(loop, color, 3, false, LINE_OPACITY), ORDER.line);
    }

    if (measurement.mode !== "line" && points.length >= 3) {
      const positions: number[] = [];
      for (let i = 1; i < points.length - 1; i++) {
        positions.push(...vec(points[0]), ...vec(points[i]), ...vec(points[i + 1]));
      }
      const geometry = new BufferGeometry();
      geometry.setAttribute("position", new Float32BufferAttribute(positions, 3));
      const fill = new Mesh(
        geometry,
        new MeshBasicMaterial({
          color,
          side: DoubleSide,
          transparent: true,
          opacity: FILL_OPACITY,
          depthWrite: false,
          depthTest: false,
        }),
      );
      this.addVisual(fill, ORDER.fill);
    }
  }

  /** Adds a visual on top regardless of what's in front, and out of `Viewer.pick()`'s way —
   *  same reasoning as `MeasureTool.addVisual`: these shapes must never steal a raycast hit
   *  meant for the terrain/mesh underneath them. */
  private addVisual(obj: Points | Line2 | Mesh, renderOrder: number): void {
    obj.renderOrder = renderOrder;
    obj.raycast = () => {};
    this.group.add(obj);
    this.visuals.push(obj);
  }

  private disposeVisuals(): void {
    for (const obj of this.visuals) {
      this.group.remove(obj);
      obj.geometry.dispose();
      for (const material of Array.isArray(obj.material) ? obj.material : [obj.material]) material.dispose();
    }
    this.visuals = [];
  }
}
